import { useTranslation } from 'react-i18next'
import { Button } from '../Button/Button'
import { ButtonLink } from '../Button/ButtonLink'
import { Icon } from '../Icon/Icon'
import type { IconName } from '../Icon/types'
import { OptionGrid } from '../OptionGrid/OptionGrid'
import { StatusChip } from '../StatusChip/StatusChip'
import { cx } from '../utils/cx'
import { ArrivalPrepCard } from './ArrivalPrepCard'
import type { ClimateControlProps, ClimateMode } from './types'
import './ClimateControl.css'

const MODE_ICON: Record<ClimateMode, IconName> = { heat: 'sun', cool: 'snowflake', auto: 'refresh-cw', off: 'power' }

/**
 * Control de clima (bundle B8, componente nuevo): sección con dos variantes sobre la misma cabecera,
 * conectada (control) y estática (instrucciones con fotos). Superficie sunken, radio 24, padding 20.
 * Botones en lugar de slider; límites explicados en texto; estados anunciados en aria-live.
 * Inventario D13: una zona por dispositivo, paso entero salvo que el backend indique medio grado,
 * "Automático" y atajos solo si existen en `modes` / `presets`.
 */
export function ClimateControl(props: ClimateControlProps) {
  const { t, i18n } = useTranslation()
  const { variant, locked, lockedChip, lockedText, arrivalPrep, zones = [], zoneId, onZoneChange, current, target, range, onTargetDelta, feedback = null, onRetry, phone, modes = [], mode, onModeChange, modesHelp, presets = [], onPreset, alert, staticInstructions, className } = props
  const isStatic = variant === 'static'
  const isControl = !isStatic && !locked
  const step = range?.step ?? 1
  const fmt = (n: number, decimals = step < 1 ? 1 : 0) =>
    `${new Intl.NumberFormat(i18n.language, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n)} °C`
  const modeLabel = (m: ClimateMode) => t(`v2.ds.climate.modo.${m}`)

  const atMin = range && target !== undefined && target <= range.min
  const atMax = range && target !== undefined && target >= range.max
  const limitText = !range
    ? null
    : atMax
      ? t('v2.ds.climate.maximo', { max: range.max, min: range.min })
      : atMin
        ? t('v2.ds.climate.minimo', { max: range.max, min: range.min })
        : step < 1
          ? t('v2.ds.climate.rangoMedio', { max: range.max, min: range.min })
          : t('v2.ds.climate.rango', { max: range.max, min: range.min })

  const chip = isStatic
    ? { tone: 'neutral' as const, icon: 'file-text' as IconName, text: t('v2.ds.climate.instrucciones') }
    : locked
      ? { tone: 'neutral' as const, icon: 'lock' as IconName, text: lockedChip }
      : { tone: 'success' as const, icon: 'check-circle' as IconName, text: t('v2.ds.climate.termostatoConectado') }

  return (
    <div role="group" aria-label={t('v2.ds.climate.titulo')} className={cx('hp-climate', className)}>
      <div className="hp-climate__head">
        <div className="hp-climate__title">
          <Icon name="thermometer" size={24} />
          <span>{t('v2.ds.climate.titulo')}</span>
        </div>
        {chip.text && (
          <StatusChip tone={chip.tone} icon={chip.icon}>
            {chip.text}
          </StatusChip>
        )}
      </div>

      {locked && !isStatic && (
        <>
          {lockedText && <p className="hp-climate__text">{lockedText}</p>}
          {arrivalPrep && (
            <ArrivalPrepCard
              target={arrivalPrep.target}
              min={arrivalPrep.min}
              max={arrivalPrep.max}
              onTargetChange={arrivalPrep.onTargetChange}
              confirmation={arrivalPrep.saved ? { text: t('v2.ds.climate.cambiarHastaLlegada') } : null}
            >
              <div className="hp-climate__arrival">
                <Icon name="clock" size={22} className="hp-climate__arrival-icon" />
                <div>
                  <div className="hp-climate__arrival-title">{arrivalPrep.arrivalText}</div>
                  <div className="hp-climate__card-text">
                    {arrivalPrep.arrivalHint ?? t('v2.ds.climate.horaRegistro')}{' '}
                    {arrivalPrep.onChangeArrival && (
                      <button type="button" onClick={arrivalPrep.onChangeArrival} className="hp-climate__link">
                        {arrivalPrep.changeArrivalLabel ?? t('v2.ds.climate.cambiarla')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <Button variant="primary" onClick={arrivalPrep.onSave} disabled={arrivalPrep.saveDisabled ?? false}>
                {arrivalPrep.saved ? t('v2.ds.climate.guardarCambio') : t('v2.ds.climate.dejarloPreparado')}
              </Button>
            </ArrivalPrepCard>
          )}
        </>
      )}

      {isControl && (
        <>
          {zones.length > 1 && onZoneChange && (
            <OptionGrid label={t('v2.ds.climate.zona')} options={zones.map((z) => ({ id: z.id, label: z.name }))} value={zoneId ?? null} onChange={onZoneChange} columns={2} className="hp-climate__zones" />
          )}
          {current && (
            <div className="hp-climate__current">
              <Icon name={MODE_ICON[current.mode]} size={40} className="hp-climate__mode-icon" />
              <div>
                <div className="hp-climate__current-value tnum">{fmt(current.temp)}</div>
                <div className="hp-climate__card-text">
                  {t('v2.ds.climate.ahoraEn', { zone: current.zoneName, mode: modeLabel(current.mode).toLowerCase() })}
                </div>
              </div>
            </div>
          )}
          {target !== undefined && range && onTargetDelta && (
            <div className="hp-climate__card">
              <div className="hp-climate__caption">{t('v2.ds.climate.temperaturaQueQuieres')}</div>
              <div className="hp-climate__stepper">
                <button
                  type="button"
                  aria-label={step < 1 ? t('v2.ds.climate.bajarMedio') : t('v2.ds.climate.bajarGrado')}
                  aria-disabled={atMin || undefined}
                  onClick={() => !atMin && onTargetDelta(-step)}
                  className={cx('hp-climate__stepbtn', atMin && 'hp-climate__stepbtn--limit')}
                >
                  <Icon name="minus" size={28} />
                </button>
                <div className="hp-climate__target tnum" aria-live="polite">
                  {fmt(target)}
                </div>
                <button
                  type="button"
                  aria-label={step < 1 ? t('v2.ds.climate.subirMedio') : t('v2.ds.climate.subirGrado')}
                  aria-disabled={atMax || undefined}
                  onClick={() => !atMax && onTargetDelta(step)}
                  className={cx('hp-climate__stepbtn', atMax && 'hp-climate__stepbtn--limit')}
                >
                  <Icon name="plus" size={28} />
                </button>
              </div>
              <p className="hp-climate__card-text">{limitText}</p>
            </div>
          )}
          <div role="status" aria-live="polite" className="hp-climate__feedback">
            {feedback === 'applying' && (
              <div className="hp-climate__status hp-climate__status--info">
                <Icon name="loader" size={22} spin />
                <div>
                  <div className="hp-climate__status-title">{t('v2.ds.climate.aplicando')}</div>
                  <div className="hp-climate__status-text">{t('v2.ds.climate.aplicandoTexto')}</div>
                </div>
              </div>
            )}
            {feedback === 'applied' && target !== undefined && current && (
              <div className="hp-climate__status hp-climate__status--success">
                <Icon name="check-circle" size={22} />
                <div>
                  <div className="hp-climate__status-title">{t('v2.ds.climate.aplicado', { temp: fmt(target), zone: current.zoneName })}</div>
                  <div className="hp-climate__status-text">{t('v2.ds.climate.aplicadoTexto')}</div>
                </div>
              </div>
            )}
            {feedback === 'error' && (
              <div className="hp-climate__status hp-climate__status--error">
                <Icon name="x-circle" size={22} />
                <div>
                  <div className="hp-climate__status-title">{t('v2.ds.climate.errorTitulo')}</div>
                  <div className="hp-climate__status-text">{t('v2.ds.climate.errorTexto')}</div>
                  <div className="hp-climate__status-actions">
                    <Button variant="primary" fullWidth={false} onClick={onRetry}>
                      {t('v2.comun.reintentar')}
                    </Button>
                    {phone && (
                      <ButtonLink variant="secondary" fullWidth={false} href={`tel:${phone.replace(/\s/g, '')}`}>
                        {t('v2.ds.contact.llamar')}
                      </ButtonLink>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {modes.length > 0 && onModeChange && (
            <OptionGrid
              label={t('v2.ds.climate.modo.titulo')}
              size="tall"
              options={modes.map((m) => ({ id: m.id, label: m.label, icon: MODE_ICON[m.id] }))}
              value={mode ?? null}
              onChange={(id) => onModeChange(id as ClimateMode)}
              columns={2}
              help={modesHelp}
            />
          )}
          {presets.length > 0 && onPreset && (
            <OptionGrid label={t('v2.ds.climate.atajos')} size="tall" options={presets.map((p) => ({ id: p.id, label: p.label, sublabel: p.sublabel }))} value={null} onChange={onPreset} columns={2} />
          )}
          {alert && (
            <div className="hp-climate__status hp-climate__status--warning">
              <Icon name="alert-triangle" size={22} />
              <div>
                <div className="hp-climate__status-title">{alert.title}</div>
                <div className="hp-climate__status-text">{alert.text}</div>
              </div>
            </div>
          )}
        </>
      )}

      {isStatic && staticInstructions && (
        <>
          <div>
            <StatusChip tone="info" icon="wifi-off">
              {t('v2.ds.climate.guardadoSinConexion')}
            </StatusChip>
          </div>
          <p className="hp-climate__text">{staticInstructions.intro}</p>
          {staticInstructions.steps}
          {staticInstructions.houseRule && (
            <div className="hp-climate__card">
              <div className="hp-climate__card-title">{t('v2.ds.climate.normaCasa')}</div>
              <p className="hp-climate__card-text">
                {staticInstructions.houseRule} {staticInstructions.contact}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
